// src/pages/PrepaidPackagesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import styles from './PrepaidPackagesPage.module.css';
import apiClient from '../services/ApiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'react-toastify';
// Vergi dahil fiyatları güvenli şekilde formatlar (string veya number gelebilir)
const formatMoney = (value) => {
    const n = parseFloat(value);
    return isNaN(n) ? '—' : n.toFixed(2);
};
// Sabit kıtalarımız
const CONTINENTS = [
    { id: 1, name: 'Avrupa' },
    { id: 2, name: 'Asya' },
    { id: 3, name: 'Kuzey Amerika' },
    { id: 4, name: 'Latin Amerika' },
    { id: 5, name: 'Afrika' },
    { id: 7, name: 'Pasifik' },
    { id: 6, name: 'Orta Doğu' },
    { id: 0, name: 'Global' },

];

const PrepaidPackagesPage = () => {
    const [selectedContinentId, setSelectedContinentId] = useState(CONTINENTS[0].id); // Başlangıçta ilk kıta seçili
    const [products, setProducts] = useState([]); // Seçilen kıtaya ait ürünler ve paketleri
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingStates, setEditingStates] = useState({});

    const [savingStates, setSavingStates] = useState({}); // { refillId1: true, refillId2: false }

    const fetchPackagesForContinent = useCallback(async (continentId) => {
        if (!continentId) return;
        setIsLoading(true);
        setError(null);
        setProducts([]); // Yeni kıta seçildiğinde eski veriyi temizle

        try {
            // Gerçek API çağrısı
            const response = await apiClient.get(`/lifetime-data/${continentId}`);

            let fetchedProducts = [];
            if (response.data && Array.isArray(response.data.data)) {
                // API'den gelen `data` dizisi, ülke ve ürünleri içeriyor gibi görünüyor.
                // Bizim amacımız bu "bölgesel ürünleri" (Andromeda, Orion vs.) almak.
                // Yanıttaki her bir 'country' objesi aslında bir 'bölgesel ürün' gibi davranıyor.
                // `products` dizisi içinde de ürünler var. Bu yapı biraz kafa karıştırıcı.
                // Yanıttaki en üst seviye diziyi ana ürünler olarak kabul edelim.
                fetchedProducts = response.data.data[0]?.products || []; // Yanıttaki `data[0].products` dizisini alıyoruz.
            } else {
                console.warn("API'den beklenen formatta veri gelmedi:", response.data);
            }

            setProducts(fetchedProducts);

            // Düzenleme state'ini ilk değerlerle doldur
            const initialEdits = {};
            fetchedProducts.forEach(product => {
                product.refills.forEach(refill => {
                    // BENZERSİZ ID: API'deki 'id' alanı artık benzersiz gibi görünüyor. Onu kullanalım.
                    const uniqueId = String(refill.id);
                    initialEdits[uniqueId] = {
                        basePriceTry: refill.basePriceTry,
                        isOpenGp: refill.isOpenGp,
                        isOpenAp: refill.isOpenAp,
                        isOpenWeb: refill.isOpenWeb
                    };
                });
            });
            setEditingStates(initialEdits);

        } catch (err) {
            console.error(`Kıta ${continentId} için paketler çekilirken hata:`, err);
            setError(`Paketler yüklenirken bir hata oluştu: ${err.response?.data?.message || err.message}`);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPackagesForContinent(selectedContinentId);
    }, [selectedContinentId, fetchPackagesForContinent]);


    const handleInputChange = (refillId, field, value) => {
        setEditingStates(prev => ({
            ...prev,
            [refillId]: {
                ...prev[refillId],
                [field]: value
            }
        }));
    };

    const handleCheckboxChange = (refillId, field, checked) => {
        setEditingStates(prev => ({
            ...prev,
            [refillId]: {
                ...prev[refillId],
                [field]: checked
            }
        }));
    };

    const handleSaveChanges = async (productId, refillId, productName, refillTitle) => {
        const editedState = editingStates[refillId];
        if (!editedState) {
            console.error("Düzenleme durumu bulunamadı:", refillId);
            return;
        }

        // Fiyat validasyonu
        const priceValueTL = parseFloat(editedState.basePriceTry);
        if (isNaN(priceValueTL) || priceValueTL < 0) { // 0 da geçerli olabilir, iş mantığınıza göre < 0
            toast.error("Lütfen geçerli bir fiyat girin.");
            return;
        }

        setSavingStates(prev => ({ ...prev, [refillId]: true })); // Bu satır için kaydetme başladı
        setError(null); // Genel hatayı temizle

        // API'ye gönderilecek request body
        const requestBody = {
            priceTL: priceValueTL,
            isOpenGp: editedState.isOpenGp,
            isOpenAp: editedState.isOpenAp,
            isOpenWeb: editedState.isOpenWeb,
            refillName: `${productName} - ${refillTitle}`, // Yeni parametre request body'ye eklendi
        };

        // Optimistic UI için orijinal veriyi sakla
        const originalProduct = products.find(p => p.originalId === productId);
        const originalRefill = originalProduct?.refills.find(r => String(r.id) === refillId);
        if (!originalRefill) return; // Güvenlik kontrolü

        // Optimistic UI: Frontend state'ini hemen güncelle
        setProducts(prevProducts =>
            prevProducts.map(product => {
                if (product.originalId === productId) {
                    return {
                        ...product,
                        refills: product.refills.map(refill =>
                            String(refill.id) === refillId ? { ...refill, ...editedState } : refill
                        )
                    };
                }
                return product;
            })
        );

        try {
            // ÖNEMLİ: Kendi PATCH endpoint'inizi kullanın
            await apiClient.patch(`/esim-packages/refills/${refillId}`, requestBody);

            toast.success(`Paket (ID: ${refillId}) başarıyla güncellendi.`);
            // Başarılı olduğunda bir şey yapmaya gerek yok, Optimistic UI zaten çalıştı.
            // editingStates'i de güncelleyebiliriz ki "değişiklik yapılmadı" kontrolü eklenecekse doğru çalışsın.
            // setEditingStates(prev => ({ ...prev, [refillId]: editedState }));

        } catch (err) {
            console.error(`Refill ID ${refillId} güncellenirken API hatası:`, err);
            setError(`Paket güncellenirken bir hata oluştu: ${err.response?.data?.message || err.message}`);

            // Hata durumunda frontend state'ini geri al
            setProducts(prevProducts =>
                prevProducts.map(product => {
                    if (product.originalId === productId) {
                        return {
                            ...product,
                            refills: product.refills.map(refill =>
                                String(refill.id) === refillId ? originalRefill : refill
                            )
                        };
                    }
                    return product;
                })
            );
        } finally {
            setSavingStates(prev => ({ ...prev, [refillId]: false })); // Kaydetme bitti
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>Ön Ödemeli eSIM Paketleri</h1>
                    <p className={styles.subtitle}>Kıta bazlı ömür boyu paketlerin fiyatlarını ve mağaza durumlarını yönetin.</p>
                </div>
                <div className={styles.infoNotes}>
                    <div className={styles.infoNote}>
                        <span className={styles.infoIcon}>ℹ️</span>
                        <span className={styles.infoText}>Girilen fiyatlar vergi hariçtir.</span>
                    </div>
                </div>
            </div>

            <div className={styles.continentSelector}>
                {CONTINENTS.map(continent => (
                    <button
                        key={continent.id}
                        className={`${styles.continentButton} ${selectedContinentId === continent.id ? styles.activeContinent : ''}`}
                        onClick={() => setSelectedContinentId(continent.id)}
                        disabled={isLoading}
                    >
                        {continent.name}
                    </button>
                ))}
            </div>
            <div className={styles.contentArea}>
                {isLoading ? (
                    <div className={styles.loadingContainer}><LoadingSpinner /></div>
                ) : error ? (
                    <div className={styles.errorPanel}>
                        <span className={styles.errorIcon}>⚠️</span>
                        <p className={styles.errorMessage}>Veriler alınamadı. Bağlantıyı kontrol edip tekrar deneyin.</p>
                        <button className={`${styles.retryButton} ghost`} onClick={() => fetchPackagesForContinent(selectedContinentId)}>Tekrar dene</button>
                    </div>
                ) : products.length === 0 ? (
                    <p className={styles.emptyState}>Bu kıta için gösterilecek bölgesel ürün bulunamadı.</p>
                ) : (
                    products.map(product => (
                        <div key={product.originalId} className={styles.productCard}>
                            <div className={styles.productHeader}>
                                <img src={product.img} alt={product.name} className={styles.productImage} />
                                <div className={styles.productInfo}>
                                    <h2 className={styles.productName}>{product.name}</h2>
                                    <p className={styles.productDescription}>{product.description}</p>
                                </div>
                            </div>
                            <div className={styles.tableWrap}>
                            <table className={styles.refillsTable}>
                                <thead>
                                    <tr>
                                        <th>Paket (Refill)</th>
                                        <th className={styles.numCol}>Fiyat (TL)</th>
                                        <th className={styles.toggleCol}>Google Play</th>
                                        <th className={styles.toggleCol}>App Store</th>
                                        <th className={styles.toggleCol}>Web</th>
                                        <th className={styles.actionCol}>İşlemler</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {product.refills.map(refill => {
                                        const uniqueId = String(refill.id);
                                        const currentEditState = editingStates[uniqueId] || {};
                                        const isSavingThisRow = savingStates[uniqueId] || false; // Bu satır kaydediliyor mu?

                                        return (
                                            <tr key={uniqueId}>
                                                <td className="num">{refill.title} ({refill.amountMb / 1024} GB)</td>
                                                <td className={styles.numCol}>
                                                    <input
                                                        type="number"
                                                        value={currentEditState.basePriceTry || ''}
                                                        onChange={(e) => handleInputChange(uniqueId, 'basePriceTry', e.target.value)}
                                                        className={styles.priceInput}
                                                        step="0.01"
                                                        disabled={isSavingThisRow} // Kaydederken pasif yap
                                                    />
                                                    <div className={styles.taxPrices} title="Vergi dahil satış fiyatları">
                                                        <span className={styles.taxLabel}>Vergi dahil</span>
                                                        <span className={`${styles.taxVals} num`}>₺{formatMoney(refill.priceTL)} · €{formatMoney(refill.priceEuro)} · ${formatMoney(refill.priceUsd)}</span>
                                                    </div>
                                                </td>
                                                <td className={styles.toggleCell}>
                                                    <label className={styles.switch}>
                                                        <input
                                                            type="checkbox"
                                                            checked={currentEditState.isOpenGp || false}
                                                            onChange={(e) => handleCheckboxChange(uniqueId, 'isOpenGp', e.target.checked)}
                                                            disabled={isSavingThisRow} // Kaydederken pasif yap
                                                        />
                                                        <span className={`${styles.slider} ${styles.round}`}></span>
                                                    </label>
                                                </td>
                                                <td className={styles.toggleCell}>
                                                    <label className={styles.switch}>
                                                        <input
                                                            type="checkbox"
                                                            checked={currentEditState.isOpenAp || false}
                                                            onChange={(e) => handleCheckboxChange(uniqueId, 'isOpenAp', e.target.checked)}
                                                            disabled={isSavingThisRow} // Kaydederken pasif yap
                                                        />
                                                        <span className={`${styles.slider} ${styles.round}`}></span>
                                                    </label>
                                                </td>
                                                <td className={styles.toggleCell}>
                                                    <label className={styles.switch}>
                                                        <input
                                                            type="checkbox"
                                                            checked={currentEditState.isOpenWeb || false}
                                                            onChange={(e) => handleCheckboxChange(uniqueId, 'isOpenWeb', e.target.checked)}
                                                            disabled={isSavingThisRow} // Kaydederken pasif yap
                                                        />
                                                        <span className={`${styles.slider} ${styles.round}`}></span>
                                                    </label>
                                                </td>
                                                <td className={styles.actionCol}>
                                                    <button
                                                        onClick={() => handleSaveChanges(product.originalId, uniqueId, product.name, refill.title)}
                                                        className={styles.saveButton}
                                                        disabled={isSavingThisRow} // Kaydederken pasif yap
                                                    >
                                                        {isSavingThisRow ? 'Kaydediliyor...' : 'Kaydet'}
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default PrepaidPackagesPage;